import type { SiteConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { PlatformSiteConfig } from '@wix/thunderbolt-symbols'
import type {
	ClientSpecMap,
	OriginalRequest,
	PagesPlatformApplications,
	RendererModel,
	TPAPublicClientSpecEntry,
	WixCodeClientSpec,
} from '@wix/thunderbolt-ssr-api'

const DATA_BINDING_APP_DEF_ID = 'dataBinding'

const platformSiteConfig: SiteConfigFn<PlatformSiteConfig> = ({ api, funcLib }) => {
	const { rendererModel, originalRequest, publicModel, rootConfig, env, builder, platform: platformApi } = api
	const clientSpecMap = rendererModel.getClientSpecMap()

	const hasPlatform = isPlatformOnSite({
		clientSpecMap,
		pagesPlatformApplications: rendererModel.getPagesPlatformApplications(),
	})

	if (!hasPlatform) {
		return {
			shouldLoadSiteFeature: false,
			shouldLoadSiteConfig: false,
			siteConfig: null,
		}
	}

	const experiments = env.getExperiments()
	const viewMode = env.getViewMode()

	// Gather all data needed for getPlatformSiteConfig
	const serviceTopology = rootConfig.getServiceTopology()
	const url = originalRequest.getUrl() ?? ''
	const componentsCount = rendererModel.getSiteMetaData()?.renderHints?.componentsCount

	// Get platform-specific data from platform store
	const platform = rootConfig.getPlatform()
	const appsUrlData = platformApi.getAppsUrlData()
	const wixCodeBundlersUrlData = platformApi.getWixCodeBundlersUrlData()

	// Check if mobile app builder
	const isMobileAppBuilder = funcLib.isMobileAppBuilder(url, componentsCount)

	// Get builder components data from builder API
	const builderComponentsData = builder.getBuilderComponentsData()

	// Build platform site config using function library
	const siteConfig = funcLib.getPlatformSiteConfig({
		hasPlatform,
		nonMainSdksStaticPath: platform.nonMainSdksStaticPath,
		mainSdksStaticPath: platform.mainSdksStaticPath,
		rendererModel: {
			clientSpecMap,
			wixCodeModel: rendererModel.getWixCodeModel(),
			pagesPlatformApplications: rendererModel.getPagesPlatformApplications(),
			hostMsid: rendererModel.getHostMsid(),
			metaSiteId: rendererModel.getMetaSiteId(),
			userId: rendererModel.getUserId(),
			siteMetaData: rendererModel.getSiteMetaData(),
			previewMode: rendererModel.getPreviewMode(),
			landingPageId: rendererModel.getLandingPageId(),
			platformControllersOnPage: rendererModel.getPlatformControllersOnPage(),
			premiumFeatures: rendererModel.getPremiumFeatures(),
			platformAppsExperiments: rendererModel.getPlatformAppsExperiments(),
			routers: rendererModel.getRouters(),
			blocksExperiments: rendererModel.getBlocksExperiments(),
		} as RendererModel,
		originalRequest: {
			url,
			headers: originalRequest.getHeaders(),
		} as OriginalRequest,
		serviceTopology,
		appsUrlData,
		isMobileView: viewMode === 'mobile',
		externalBaseUrl: publicModel.getExternalBaseUrl() ?? '',
		clientWorkerUrl: platform.clientWorkerUrl,
		experiments,
		wixCodeBundlersUrlData,
		requestId: publicModel.getRequestId() ?? '',
		isMobileAppBuilder,
		builderComponentsData,
	})

	return {
		shouldLoadSiteFeature: true,
		shouldLoadSiteConfig: true,
		siteConfig,
	}
}

/**
 * Determines if platform should load on the site
 *
 * Platform loads if ANY of these conditions are true:
 * 1. Some application installed (with viewerScriptUrl and not optional)
 * 2. WixCode or DataBinding installed
 * 3. Client SDK required (widgets with dependencies)
 */
function isPlatformOnSite({
	clientSpecMap,
	pagesPlatformApplications,
}: {
	clientSpecMap: ClientSpecMap
	pagesPlatformApplications?: PagesPlatformApplications
}): boolean {
	// Check if some application is installed
	const isSomeApplicationInstalled = Object.values(clientSpecMap).some((app) => {
		const viewerScriptUrl = (app as TPAPublicClientSpecEntry)?.appFields?.platform?.viewerScriptUrl
		const isOptional = (app as TPAPublicClientSpecEntry)?.appFields?.platform?.optionalApplication === true
		return viewerScriptUrl && !isOptional
	})

	// Check if WixCode or DataBinding is installed
	const isWixCodeOrDataBindingInstalled = (() => {
		// Find wixCode app (type === 'siteextension')
		const wixCodeApp = Object.values(clientSpecMap).find((app) => app.type === 'siteextension') as WixCodeClientSpec
		const wixCodeAppDefinitionId = wixCodeApp?.appDefinitionId

		if (!pagesPlatformApplications) {
			return false
		}

		// Get all apps across all pages
		const appsOnSite = Object.values(pagesPlatformApplications).flat()

		// Check if dataBinding or wixCode is in the apps list
		const hasDataBinding = appsOnSite.includes(DATA_BINDING_APP_DEF_ID)
		const hasWixCode = wixCodeAppDefinitionId && appsOnSite.includes(wixCodeAppDefinitionId)

		return hasDataBinding || hasWixCode
	})()

	// Check if client SDK is required
	const shouldLoadClientSdk = shouldLoadClientSdkHelper(clientSpecMap)

	return isSomeApplicationInstalled || isWixCodeOrDataBindingInstalled || shouldLoadClientSdk
}

/**
 * Helper to determine if client SDK should load
 * Checks if any widget has dependencies defined
 */
function shouldLoadClientSdkHelper(clientSpecMap: ClientSpecMap): boolean {
	return Object.values(clientSpecMap).some((app) => {
		const widgets = (app as TPAPublicClientSpecEntry)?.widgets
		if (!widgets || typeof widgets !== 'object') {
			return false
		}

		return Object.values(widgets).some((widget) => {
			const dependencies = widget?.componentFields?.dependencies
			return dependencies && Array.isArray(dependencies) && dependencies.length > 0
		})
	})
}

export const name = 'platform'

export const config = {
	name,
	site: platformSiteConfig,
}
